/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, memo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import GroupProperties, {getAssetTypes} from '@ion/core/src/services/GroupProperties';
import FilterEmptyState from '@ion/dls/src/filters/FilterEmptyState';
import {createFilterOptions} from '@ion/dls/src/filters/FilterHelpers';
import {FilterAssetTypeIcon} from '@ion/dls/src/icons/current/FilterAssetTypeIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {IconSize} from '@ion/dls/src/types/types';
import changePage, {Route} from '@/routes/RouteDefinitions';
import {setAssetTypeFilters} from '@/store/actions/tracking';
import {globalGroupIdSelector} from '@/store/selectors/global-group-chooser-selectors';
import {trackingFiltersSelector} from '@/store/selectors/tracking-selectors';
import TrackingDrawerFilter, {
  TrackingDrawerFilterHandle,
  TrackingDrawerFilterOption
} from './TrackingDrawerFilter';
import {TrackingFilterNavigationRequest, TrackingFilterStateKey} from './types';
import {requestTrackingFilterNavigation} from './utils';

export type TrackingAssetTypeFilterHandle = TrackingDrawerFilterHandle;

export interface TrackingAssetTypeFilterProps {
  onRequestNavigate?: TrackingFilterNavigationRequest;
  onSelectedCountChange?: (count: number) => void;
  onSelectionChange?: () => void;
}

export const TrackingAssetTypeFilter = forwardRef<TrackingAssetTypeFilterHandle, TrackingAssetTypeFilterProps>(({
  onRequestNavigate,
  onSelectedCountChange,
  onSelectionChange
}, ref) => {
  // re-renders on a light/dark switch, so the filter icon color follows the mode ...
  useThemeMode();
  const globalGroupId      = useSelector(globalGroupIdSelector);
  const trackingFilters    = useSelector(trackingFiltersSelector);
  const selectedAssetTypes = trackingFilters?.assetTypes?.selected ?? [];

  const loadOptions = useCallback(async(): Promise<TrackingDrawerFilterOption[]> => {
    if (!globalGroupId) {
      return [];
    }
    await GroupProperties.getInheritedProperties(globalGroupId);
    const assetTypes = await getAssetTypes();
    return createFilterOptions(assetTypes as any);
  }, [globalGroupId]);

  const handleManageAssetTypes = useCallback(function() {
    requestTrackingFilterNavigation(function() {
      changePage(Route.ADMIN_GROUPS);
    }, onRequestNavigate);
  }, [onRequestNavigate]);

  return (
    <TrackingDrawerFilter ref={ref}
      applyFilter={setAssetTypeFilters}
      filterStateKey={TrackingFilterStateKey.AssetTypes}
      emptyState={(
        <FilterEmptyState title={translate('filters:tracking.assetType.empty.title')}
          description={translate('filters:tracking.assetType.empty.description')}
          actionLabel={translate('filters:tracking.assetType.empty.action')}
          icon={<FilterAssetTypeIcon color={dlsColor('grey1')} size={IconSize.medium} />}
          onAction={handleManageAssetTypes} />
      )}
      loadOptions={loadOptions}
      selectedItems={selectedAssetTypes}
      searchPlaceholder={translate('filters:tracking.assetType.searchPlaceholder')}
      onSelectedCountChange={onSelectedCountChange}
      onSelectionChange={onSelectionChange} />
  );
});

export default memo(TrackingAssetTypeFilter);
